import { Stack } from '@fluentui/react';
import { IdentityAdapter } from 'adapters/identity-adapter/identity-adapter';
import { ResourceAdapter } from 'adapters/resource-adapter/resource-adapter';
import TrialPeriodExpired from 'components/TrialPeriodExpired/TrialPeriodExpired';
import LoadingDialog from 'components/LoadingDialog/LoadingDialog';
import AreaSwitch from 'features/Areas/AreaSwitch/AreaSwitch';
import { ConnectivityModal } from 'features/ConnectivityModal/ConnectivityModal';
import InactivityRulesHost from 'features/InactivityRules/InactivityRulesHost';
import OooStatusReminderModal from 'features/Rooms/RoomList/UserFace/UserSelfStatus/OooStatusReminderModal';
import IvicosStrings from 'kits/language/stringKit';
import React, { useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import CoreView from 'routes/Core/Core.view';
import { useCanUseStatus, useIdentityClaims } from 'shared-state/identity/hooks';
import { StatusPersistenceProvider } from 'shared-state/identity/StatusPersistenceProvider';
import SocketProvider from 'services/socket-connection/context/SocketProvider';
import SocketAdapter from 'services/socket-connection/socket-adapters';
import MatrixProvider from 'services/matrix-connection/context/MatrixProvider';
import MatrixAdapter from 'adapters/matrix-adapter/matrix-adapter';

const CORE_ROUTE_MESSAGES = [
    { pattern: { path: '/dashboard/members', end: true }, message: () => IvicosStrings.loadingStringCoreDashboardMembers },
    { pattern: { path: '/dashboard/settings', end: true }, message: () => IvicosStrings.loadingStringCoreDashboardSettings },
    { pattern: { path: '/dashboard/billing', end: true }, message: () => IvicosStrings.loadingStringCoreDashboardBilling },
    { pattern: { path: '/dashboard/changelog', end: true }, message: () => IvicosStrings.loadingStringCoreDashboardChangelog },
    // Lobby copy for `/dashboard`, `/dashboard/areas`, and other dashboard tabs not matched above (`end: false` must stay after specific `/dashboard/*` routes).
    { pattern: { path: '/dashboard', end: false }, message: () => IvicosStrings.loadingStringCoreDashboardLobby },
    { pattern: { path: '/areas/:areaId/rooms/:roomId', end: true }, message: () => IvicosStrings.loadingStringCoreRoom },
    { pattern: { path: '/areas/:areaId', end: true }, message: () => IvicosStrings.loadingStringCoreArea },
    { pattern: { path: '/group', end: false }, message: () => IvicosStrings.loadingStringSubAreas }
];

const coreLoadingMessage = (pathname: string): string =>
    CORE_ROUTE_MESSAGES.find(({ pattern }) => matchPath(pattern, pathname))?.message() ?? IvicosStrings.loadingStringCoreInitializing;

const Core: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
    const { pathname } = useLocation();
    const [identity] = useIdentityClaims();
    const canUseStatus = useCanUseStatus();

    const [isLoadingDialogVisible, setIsLoadingDialogVisible] = useState(true);
    const [isOrgStandby, setIsOrgStandby] = useState(['standby'].includes(identity?.orgStatus));

    useEffect(() => {
        setIsOrgStandby(['standby'].includes(identity?.orgStatus));
    }, [identity]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsLoadingDialogVisible(false);
        }, 1000);
        return () => clearTimeout(timer);
    }, []);

    const onRenderTopBarDocumentPicker = (textColor?: string, isLowerTopBar?: boolean) =>
        identity ? <AreaSwitch color={textColor} isLowerTopBar={isLowerTopBar} /> : <></>;

    return (
        <Stack style={{ height: '100vh' }}>
            {isLoadingDialogVisible && <LoadingDialog loadingStrings={[coreLoadingMessage(pathname)]} view="full" typewriter={false} />}
            <IdentityAdapter />
            {isOrgStandby ? (
                <TrialPeriodExpired identity={identity} />
            ) : (
                <StatusPersistenceProvider>
                    <ResourceAdapter />
                    <ConnectivityModal />
                    <SocketProvider>
                        <MatrixProvider>
                            <MatrixAdapter />
                            <SocketAdapter />
                            {canUseStatus && <InactivityRulesHost />}
                            {canUseStatus && <OooStatusReminderModal />}
                            <CoreView onRenderTopBarDocumentPicker={onRenderTopBarDocumentPicker}>{children}</CoreView>
                        </MatrixProvider>
                    </SocketProvider>
                </StatusPersistenceProvider>
            )}
        </Stack>
    );
};

export default Core;
